import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddSubscriptionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch hospitals
      const hospitalsRes = await axios.get(`${API}/subscriptions/hospitals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHospitals(hospitalsRes.data.hospitals);
      
      // Fetch plans
      const plansRes = await axios.get(`${API}/subscriptions/plans`);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async () => {
    if (!selectedHospital || !selectedPlan) {
      toast.error('Please select institution and plan');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(endDate) <= new Date(startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/create-for-hospital`,
        {
          hospital_admin_id: selectedHospital,
          plan_id: selectedPlan,
          billing_cycle: billingCycle,
          start_date: startDate,
          end_date: endDate
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            hospital_admin_id: selectedHospital,
            plan_id: selectedPlan,
            billing_cycle: billingCycle
          }
        }
      );
      
      setSuccess(true);
      toast.success('Subscription created successfully');
      
      setTimeout(() => {
        navigate('/subscriptions/manage');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-[22px] font-bold mb-2" style={{ color: '#0056B3' }}>Subscription Created!</h2>
          <p className="text-gray-600 mb-6">The subscription has been created successfully.</p>
          <p className="text-sm text-gray-500">Redirecting...</p>
        </Card>
      </div>
    );
  }

  const selectedPlanData = plans.find(p => p.plan_id === selectedPlan);
  const price = selectedPlanData ? (billingCycle === 'monthly' ? selectedPlanData.price_monthly : selectedPlanData.price_yearly) : 0;

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Add Subscription</h1>
            <Button onClick={() => navigate('/subscriptions/manage')} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>Create New Subscription</h2>

          <div className="space-y-6">
            {/* Institution Selection */}
            <div>
              <Label>Select Institution *</Label>
              <Select value={selectedHospital} onValueChange={setSelectedHospital}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an institution" />
                </SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(hospitals, 'institution_name').map(hospital => (
                    <SelectItem key={hospital.id} value={hospital.id}>
                      {hospital.institution_name} ({hospital.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Plan Selection */}
            <div>
              <Label>Select Plan *</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(plans, 'plan_name').map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.plan_name} - ₹{billingCycle === 'monthly' ? plan.price_monthly.toLocaleString() : plan.price_yearly.toLocaleString()}/{billingCycle === 'monthly' ? 'month' : 'year'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Billing Cycle */}
            <div>
              <Label className="mb-3 block">Billing Cycle *</Label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'monthly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Monthly</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'monthly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'monthly' && (
                        <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Billed monthly</p>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'yearly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setBillingCycle('yearly')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Yearly</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'yearly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'yearly' && (
                        <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Save 10% annually</p>
                </div>
              </div>
            </div>

            {/* Start Date */}
            <div>
              <Label>Subscription Start Date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">The date when the subscription becomes active</p>
            </div>

            {/* End Date */}
            <div>
              <Label>Subscription End Date *</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">The date when the subscription expires and requires renewal</p>
            </div>

            {/* Price Summary */}
            {selectedPlanData && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Plan Price:</span>
                  <span className="font-semibold">₹{price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">GST (18%):</span>
                  <span className="font-semibold">₹{(price * 0.18).toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold text-lg" style={{ color: '#0056B3' }}>
                    ₹{(price * 1.18).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex space-x-4 pt-4">
              <Button
                onClick={() => navigate('/subscriptions/manage')}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={loading || !selectedHospital || !selectedPlan || !startDate || !endDate}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </>
                ) : (
                  'Create Subscription'
                )}
              </Button>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AddSubscriptionPage;
